import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  private isExtensionError(error: Error): boolean {
    const errorStr = error?.message || error?.stack || String(error);
    const extensionKeywords = [
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'extension://',
      'has not been authorized yet',
      'page.js',
      'content-script.js',
      'injected.js',
      'pageProvider.js',
      'Backpack',
      'MetaMask',
      'Starknet',
      'User rejected',
      'window.ethereum',
      'onhogfjeacnfoofkfgppdlbmlmnplgbn'
    ];

    return extensionKeywords.some(keyword => errorStr.includes(keyword));
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's an extension error
    const errorStr = error?.message || error?.stack || String(error);
    const extensionKeywords = [
      'chrome-extension://',
      'moz-extension://',
      'has not been authorized yet',
      'page.js',
      'extension://',
      'onhogfjeacnfoofkfgppdlbmlmnplgbn'
    ];

    const isExtensionError = extensionKeywords.some(keyword => errorStr.includes(keyword));
    
    if (isExtensionError) {
      // Don't update state for extension errors - just ignore them
      return { hasError: false };
    }

    // Update state for legitimate errors
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log legitimate errors, ignore extension errors
    if (!this.isExtensionError(error)) {
      console.error('Legitimate error caught by ErrorBoundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError && this.state.error && !this.isExtensionError(this.state.error)) {
      return (
        <div className="min-h-screen bg-[var(--dusk-bg)] flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Something went wrong</h1>
            <p className="text-gray-300 mb-4">
              An error occurred while loading the application.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
